import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { buildSheinTemplateChildPath } from '@/shein-platform/constants/sheinRoutes';
import { loadProductMetaBySkc } from '@/shein-platform/utils/sheinProductMetaBySkc';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import { showToast } from '@/utils/toast';

const COMBO_MENU_GAP = 4;
const COMBO_MENU_MAX_HEIGHT = 220;
const COMBO_VIEWPORT_PADDING = 8;

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

function splitSkcNames(value) {
  if (!value) return [];
  return String(value)
    .split(/[,\s，]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function certDisplayName(row) {
  const files = Array.isArray(row?.fileList) ? row.fileList : [];
  const fromFiles = files.map((f) => f?.fileName).filter(Boolean);
  return row?.name
    || row?.fileName
    || fromFiles[0]
    || row?.title
    || row?.poolSn
    || i18n.t('certForm.unnamed', { ns: 'workbench' });
}

/**
 * 可输入筛选的证书名称下拉；菜单挂载到 body，避免被弹窗 overflow 裁切。
 */
function CertificateNameCombobox({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder = '',
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);

  const selected = useMemo(
    () => options.find((item) => String(item.poolSn) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) => {
      const hay = `${item.label || ''} ${item.poolSn || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const updateMenuPosition = useCallback(() => {
    const trigger = inputRef.current;
    const menu = menuRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || COMBO_MENU_MAX_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom - COMBO_VIEWPORT_PADDING;
    const spaceAbove = rect.top - COMBO_VIEWPORT_PADDING;
    const openUp = spaceBelow < Math.min(menuHeight, COMBO_MENU_MAX_HEIGHT) && spaceAbove > spaceBelow;

    let top;
    if (openUp) {
      top = Math.max(COMBO_VIEWPORT_PADDING, rect.top - COMBO_MENU_GAP - menuHeight);
    } else {
      top = Math.min(
        window.innerHeight - COMBO_VIEWPORT_PADDING - Math.min(menuHeight, COMBO_MENU_MAX_HEIGHT),
        rect.bottom + COMBO_MENU_GAP,
      );
    }

    setMenuStyle({
      top: `${top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery(selected?.label || '');
    }
  }, [open, selected]);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, disabled, filtered.length, loading, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const menu = open && !disabled ? (
    <div
      ref={menuRef}
      className="spo-cert-name-combo__menu spo-cert-name-combo__menu--portal"
      role="listbox"
      style={menuStyle || { visibility: 'hidden' }}
    >
      {loading ? (
        <div className="spo-cert-name-combo__empty">{t('returnList.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="spo-cert-name-combo__empty">{t('certForm.noMatchCert')}</div>
      ) : (
        filtered.map((item) => (
          <button
            key={item.poolSn}
            type="button"
            role="option"
            className={`spo-cert-name-combo__option${String(item.poolSn) === String(value) ? ' is-active' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange?.(item.poolSn);
              setQuery(item.label);
              setOpen(false);
            }}
          >
            <span className="spo-cert-name-combo__name">{item.label}</span>
            <span className="spo-muted spo-cert-name-combo__sn">{item.poolSn}</span>
          </button>
        ))
      )}
    </div>
  ) : null;

  return (
    <div className={`spo-cert-name-combo${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <input
        ref={inputRef}
        className="spo-input"
        type="text"
        disabled={disabled}
        value={open ? query : (selected?.label || query || '')}
        placeholder={loading ? t('certForm.loadingCerts') : (placeholder || t('certForm.filterPh'))}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
          setQuery(selected?.label || '');
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange?.('');
        }}
        autoComplete="off"
      />
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}

/**
 * 产品合规 · 证书绑定：按商品货号推导款号，从证书库带出同款号证书并绑定到 SKC。
 */
export default function CertificateSavePanel({
  open = false,
  shopId,
  seed = null,
  onSaved,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [skcName, setSkcName] = useState('');
  const [certificateTypeId, setCertificateTypeId] = useState('');
  const [poolSn, setPoolSn] = useState('');
  const [styleKey, setStyleKey] = useState('');
  const [styleNoLabel, setStyleNoLabel] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const autoPickedRef = useRef(false);

  const libraryPath = buildSheinTemplateChildPath('certificates');

  useEffect(() => {
    if (!open) return;
    const fromNames = splitSkcNames(seed?.skcNames);
    const fromName = splitSkcNames(seed?.skcName);
    const skcs = fromNames.length ? fromNames : fromName;
    setSkcName(skcs.join(','));
    setCertificateTypeId(seed?.certificateTypeId != null ? String(seed.certificateTypeId) : '');
    setPoolSn(seed?.poolSn || '');
    setStyleKey('');
    setStyleNoLabel('');
    setCertOptions([]);
    autoPickedRef.current = Boolean(seed?.poolSn);
  }, [open, seed]);

  useEffect(() => {
    if (!open || !shopId) return;
    sheinPlatformAPI.listCertificateTypes(shopId)
      .then((res) => setTypeOptions(parseTypeOptions(res)))
      .catch(() => setTypeOptions([]));
  }, [open, shopId]);

  // 按 SKC 货号推导款号，用于匹配证书库
  useEffect(() => {
    if (!open || !shopId) {
      setStyleKey('');
      setStyleNoLabel('');
      return undefined;
    }
    const skcs = splitSkcNames(skcName);
    if (!skcs.length) {
      setStyleKey('');
      setStyleNoLabel('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const metaMap = await loadProductMetaBySkc(shopId, skcs.slice(0, 20));
        if (cancelled) return;
        let supplierCode = '';
        for (const skc of skcs) {
          const code = String(metaMap?.[skc]?.supplierCode || '').trim();
          if (code) {
            supplierCode = code;
            break;
          }
        }
        const styleNo = String(styleNoFromSupplierCode(supplierCode) || '').trim();
        setStyleNoLabel(styleNo);
        setStyleKey(styleNo.toLowerCase());
      } catch {
        if (!cancelled) {
          setStyleKey('');
          setStyleNoLabel('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, skcName]);

  useEffect(() => {
    if (!open || !shopId || !certificateTypeId) {
      setCertOptions([]);
      return undefined;
    }
    let cancelled = false;
    setCertsLoading(true);
    // 优先拉有款号的可绑定模板；失败再退回按类型的平台列表
    Promise.all([
      sheinPlatformAPI.listCertificateLibrary(shopId, {
        source: 'style-bind',
        page: 1,
        pageSize: 200,
      }).catch(() => null),
      sheinPlatformAPI.listCertificateLibrary(shopId, {
        source: 'platform',
        page: 1,
        pageSize: 100,
        certificateTypeId,
      }).catch(() => null),
    ])
      .then(([styleRes, typeRes]) => {
        if (cancelled) return;
        const styleList = Array.isArray(styleRes?.list) ? styleRes.list : [];
        const typeList = Array.isArray(typeRes?.list) ? typeRes.list : [];
        const typeId = String(certificateTypeId);
        const byPool = new Map();
        const pushRow = (row, preferStyle = false) => {
          const sn = String(row?.poolSn || '').trim();
          if (!sn) return;
          const rowType = String(row?.certificateTypeId ?? '').trim();
          if (rowType && rowType !== typeId) return;
          const styleNo = String(row?.styleNo || '').trim();
          const prev = byPool.get(sn);
          if (prev && !preferStyle) return;
          byPool.set(sn, {
            poolSn: sn,
            label: certDisplayName(row) + (styleNo ? ` · ${styleNo}` : ''),
            certificateTypeId: row.certificateTypeId,
            certificateTypeCode: row.certificateTypeCode,
            styleNo,
            styleMatched: Boolean(styleKey && styleNo.toLowerCase() === styleKey),
          });
        };
        styleList.forEach((row) => pushRow(row, true));
        typeList.forEach((row) => pushRow(row, false));
        const options = [...byPool.values()].sort((a, b) => {
          if (a.styleMatched !== b.styleMatched) return a.styleMatched ? -1 : 1;
          return String(a.label).localeCompare(String(b.label), 'zh');
        });
        setCertOptions(options);

        // 已有 seed.poolSn 或用户已选手动选择时不覆盖
        if (autoPickedRef.current) return;
        const matched = options.filter((item) => item.styleMatched);
        if (matched.length === 1) {
          setPoolSn(matched[0].poolSn);
          autoPickedRef.current = true;
          showToast(t('certForm.matchedOne', { name: matched[0].label }), 'success');
        } else if (matched.length > 1) {
          setPoolSn(matched[0].poolSn);
          autoPickedRef.current = true;
          showToast(t('certForm.matchedMany', { count: matched.length, name: matched[0].label }), 'info');
        }
      })
      .catch(() => {
        if (!cancelled) setCertOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCertsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, certificateTypeId, styleKey, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const submit = async () => {
    if (!shopId) return;
    const skcNames = splitSkcNames(skcName);
    if (!skcNames.length) {
      showToast(t('certForm.missingSkc'), 'error');
      return;
    }
    if (!certificateTypeId) {
      showToast(t('certForm.pickType'), 'error');
      return;
    }
    if (!poolSn?.trim()) {
      showToast(t('certForm.pickName'), 'error');
      return;
    }
    setSaving(true);
    try {
      await sheinPlatformAPI.bindGoodsCertificates(shopId, {
        poolSn: poolSn.trim(),
        skcNames,
      });
      showToast(t('certForm.boundOk', { no: poolSn.trim() }), 'success');
      onSaved?.();
    } catch (e) {
      showToast(e?.message || t('shop.bindFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-cert-modal-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-cert-modal-title">{t('certList.bind')}</h2>
          <button
            type="button"
            className="spo-btn spo-btn--ghost spo-btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="spo-batch-modal__body spo-cert-modal__body">
          <p className="spo-batch-modal__hint">
            {t('certForm.styleHint')}
            <Link className="spo-hint-link" to={libraryPath}>{t('certForm.library')}</Link>
            。
            {styleNoLabel ? (
              <span className="spo-batch-modal__hint-meta">{t('certForm.currentStyle', { no: styleNoLabel })}</span>
            ) : null}
          </p>

          <div className="spo-cert-modal__grid">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">SKC <RequiredMark /></span>
              <input
                className="spo-input"
                type="text"
                value={skcName}
                disabled
                readOnly
                placeholder={t('certForm.skcPh')}
              />
            </label>

            <label className="spo-field">
              <span className="spo-field__label">{t('certLib.colType')} <RequiredMark /></span>
              <select
                className="spo-select"
                value={certificateTypeId}
                disabled={saving}
                onChange={(e) => {
                  setCertificateTypeId(e.target.value);
                  setPoolSn('');
                  autoPickedRef.current = false;
                }}
              >
                <option value="">{t('shop.pleaseSelect')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={item.certificateTypeId}>
                    {item.certificateType || item.certificateTypeName || item.certificateTypeId}
                  </option>
                ))}
              </select>
            </label>

            <label className="spo-field">
              <span className="spo-field__label">{t('certForm.certName')} <RequiredMark /></span>
              <CertificateNameCombobox
                options={certOptions}
                value={poolSn}
                disabled={saving || !certificateTypeId}
                loading={certsLoading}
                placeholder={
                  certificateTypeId
                    ? (styleKey ? t('certForm.styleFirst') : t('certForm.filterPh'))
                    : t('certForm.pickTypeFirst')
                }
                onChange={(next) => {
                  autoPickedRef.current = Boolean(next);
                  setPoolSn(next);
                }}
              />
            </label>
          </div>
        </div>

        <div className="spo-batch-modal__foot">
          <button
            type="button"
            className="spo-btn spo-btn--secondary spo-btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={submit}
            disabled={saving || !shopId || !skcName}
          >
            {saving ? t('certForm.binding') : t('certForm.confirmBind')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
